(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const setupTextPressure = (entry) => {
    const container = entry.querySelector('[data-text-pressure]');
    if (!container || container.dataset.pressureReady === 'true') return () => {};

    const text = container.dataset.text || container.textContent.trim();
    const title = document.createElement('span');
    const letters = [];
    const words = text.split(/\s+/).filter(Boolean);

    words.forEach((wordText, wordIndex) => {
      const word = document.createElement('span');
      word.className = 'shouldi-entry-screen__word';

      [...wordText].forEach((character) => {
        const letter = document.createElement('span');
        letter.className = 'shouldi-entry-screen__letter';
        letter.textContent = character;
        letter.dataset.char = character;
        word.appendChild(letter);
        letters.push(letter);
      });

      title.appendChild(word);

      if (wordIndex < words.length - 1) {
        const space = document.createElement('span');
        space.className = 'shouldi-entry-screen__letter is-space';
        space.textContent = '\u00a0';
        space.dataset.char = ' ';
        title.appendChild(space);
        letters.push(space);
      }
    });

    title.className = 'shouldi-entry-screen__pressure-title';
    container.replaceChildren(title);
    container.dataset.pressureReady = 'true';
    container.dataset.splitWordmark = String(words.length === 2);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return () => {};

    let current = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    let target = { ...current };
    let animationFrame;
    let isActive = true;
    let letterCenters = [];
    let maxDistance = 1;

    const measureText = () => {
      const bounds = container.getBoundingClientRect();
      current = { x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 };
      target = { ...current };
      const titleBounds = title.getBoundingClientRect();
      maxDistance = Math.max(titleBounds.width / 2, 1);
      letterCenters = letters.map((letter) => {
        const letterBounds = letter.getBoundingClientRect();
        return {
          x: letterBounds.left + letterBounds.width / 2,
          y: letterBounds.top + letterBounds.height / 2,
        };
      });
    };

    const handlePointerMove = (event) => {
      target.x = event.clientX;
      target.y = event.clientY;
    };

    const handleTouchMove = (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      target.x = touch.clientX;
      target.y = touch.clientY;
    };

    const animate = () => {
      if (!isActive) return;

      current.x += (target.x - current.x) / 15;
      current.y += (target.y - current.y) / 15;

      letters.forEach((letter, index) => {
        const center = letterCenters[index];
        const distance = Math.min(Math.hypot(current.x - center.x, current.y - center.y), maxDistance);
        const pressure = 1 - distance / maxDistance;
        const widthAxis = Math.round(92 + pressure * 12);
        const weightAxis = Math.round(620 + pressure * 180);
        const opticalSize = Math.round(8 + pressure * 136);
        const scaleX = 0.96 + pressure * 0.08;
        const skew = -2 * pressure;

        letter.style.fontVariationSettings = `'opsz' ${opticalSize}, 'wdth' ${widthAxis}, 'wght' ${weightAxis}`;
        letter.style.fontWeight = weightAxis;
        letter.style.transform = `scaleX(${scaleX.toFixed(3)}) skewX(${skew.toFixed(2)}deg)`;
      });

      animationFrame = window.requestAnimationFrame(animate);
    };

    measureText();
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('resize', measureText);
    animate();

    return () => {
      if (!isActive) return;
      isActive = false;
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('resize', measureText);
    };
  };

  const setupEntryScreen = (entry) => {
    if (entry.dataset.entryReady === 'true') return;

    const closeButton = entry.querySelector('[data-entry-close]');
    const displayOnce = entry.dataset.displayOnce === 'true';
    const storageKey = entry.dataset.storageKey || 'shouldi-entry';
    const duration = Math.max(1000, Number(entry.dataset.duration) || 1500);
    const entryImage = entry.querySelector('.shouldi-entry-screen__media img');
    const inThemeEditor = Boolean(window.Shopify?.designMode);
    let hasBeenSeen = false;
    let closeTimer;
    let isClosed = false;

    try {
      hasBeenSeen = displayOnce && window.sessionStorage.getItem(storageKey) === 'seen';
    } catch (error) {
      hasBeenSeen = false;
    }

    entry.dataset.entryReady = 'true';
    if (hasBeenSeen && !inThemeEditor) return;

    const stopTextPressure = setupTextPressure(entry);

    const closeEntry = () => {
      if (isClosed) return;
      isClosed = true;
      window.clearTimeout(closeTimer);
      document.removeEventListener('keydown', handleKeydown);
      entryImage?.removeEventListener('load', startCloseTimer);
      entryImage?.removeEventListener('error', startCloseTimer);
      stopTextPressure();
      if (document.body.classList.contains('template-index')) {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
      entry.classList.add('is-leaving');
      entry.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('shouldi-entry-open');
      document.dispatchEvent(new CustomEvent('shouldi:entry-closed'));

      try {
        if (displayOnce) window.sessionStorage.setItem(storageKey, 'seen');
      } catch (error) {
        // The screen still closes when storage is unavailable.
      }

      window.setTimeout(() => {
        entry.classList.remove('is-visible', 'is-leaving');
      }, 500);
    };

    const handleKeydown = (event) => {
      if (event.key !== 'Escape') return;
      closeEntry();
    };

    document.body.classList.add('shouldi-entry-open');
    if (document.body.classList.contains('template-index')) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    window.requestAnimationFrame(() => {
      entry.classList.add('is-visible');
      entry.setAttribute('aria-hidden', 'false');
    });

    closeButton?.addEventListener('click', closeEntry, { once: true });
    document.addEventListener('keydown', handleKeydown);

    const startCloseTimer = () => {
      if (!inThemeEditor && !isClosed) closeTimer = window.setTimeout(closeEntry, duration);
    };

    if (entryImage && !entryImage.complete) {
      entryImage.addEventListener('load', startCloseTimer, { once: true });
      entryImage.addEventListener('error', startCloseTimer, { once: true });
    } else {
      startCloseTimer();
    }
  };

  const setupEntryScreens = (scope = document) => {
    scope.querySelectorAll('shouldi-entry-screen').forEach(setupEntryScreen);
  };

  const setupMobileAddToCart = () => {
    const main = document.querySelector('main[data-template="product"]');
    const sourceButton = document.querySelector('product-form button[name="add"]');
    if (!main || !sourceButton) return;

    const productTitle = document.querySelector('.product__title h1')?.textContent?.trim() || window.shouldiStrings?.selectedItem || document.title;
    const price = document.querySelector('.product__info-container .price .price-item--regular')?.textContent?.trim() || '';
    const bar = document.createElement('div');
    bar.className = 'shouldi-mobile-atc';
    bar.innerHTML = `
      <div class="shouldi-mobile-atc__info">
        <strong>${productTitle}</strong>
        <span>${price}</span>
      </div>
      <button type="button"></button>
    `;

    const proxyButton = bar.querySelector('button');
    const syncButton = () => {
      const sourceText = sourceButton.querySelector('span')?.textContent?.trim() || sourceButton.textContent.trim();
      proxyButton.textContent = sourceText;
      proxyButton.disabled = sourceButton.disabled;
      const currentPrice = document.querySelector('.product__info-container .price .price-item--regular')?.textContent?.trim();
      if (currentPrice) bar.querySelector('.shouldi-mobile-atc__info span').textContent = currentPrice;
    };

    proxyButton.addEventListener('click', () => sourceButton.click());
    document.body.appendChild(bar);
    document.body.classList.add('shouldi-has-mobile-atc');
    syncButton();

    const syncChatOffset = (isVisible) => {
      document.body.classList.toggle('shouldi-mobile-atc-visible', isVisible);
      const chatSelectors = [
        '#ShopifyChat',
        'shopify-chat',
        '[data-shopify-chat]',
        'iframe[title*="chat" i]',
        'iframe[src*="inbox" i]',
      ];
      document.querySelectorAll(chatSelectors.join(',')).forEach((chat) => {
        if (isVisible) {
          chat.dataset.shouldiOriginalBottom = chat.style.bottom || '';
          chat.style.setProperty('bottom', 'calc(7.8rem + env(safe-area-inset-bottom))', 'important');
        } else if (chat.dataset.shouldiOriginalBottom !== undefined) {
          chat.style.bottom = chat.dataset.shouldiOriginalBottom;
          delete chat.dataset.shouldiOriginalBottom;
        }
      });
    };

    const buttonObserver = new MutationObserver(syncButton);
    buttonObserver.observe(sourceButton, { attributes: true, childList: true, subtree: true });

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        const isVisible = !entry.isIntersecting;
        bar.classList.toggle('is-visible', isVisible);
        syncChatOffset(isVisible);
      },
      { threshold: 0.15 }
    );
    visibilityObserver.observe(sourceButton);

    const chatObserver = new MutationObserver(() => syncChatOffset(bar.classList.contains('is-visible')));
    chatObserver.observe(document.body, { childList: true, subtree: true });
  };

  const setupHeroCarousel = (carousel) => {
    if (carousel.dataset.carouselReady === 'true') return;

    const slides = [...carousel.querySelectorAll('[data-hero-slide]')];
    const captions = [...carousel.querySelectorAll('[data-hero-caption]')];
    const dots = [...carousel.querySelectorAll('[data-hero-dot]')];
    const previousButton = carousel.querySelector('[data-hero-previous]');
    const nextButton = carousel.querySelector('[data-hero-next]');
    const autoplayButton = carousel.querySelector('[data-hero-autoplay]');
    const pauseIcon = carousel.querySelector('[data-hero-pause-icon]');
    const playIcon = carousel.querySelector('[data-hero-play-icon]');
    const count = carousel.querySelector('[data-hero-count]');
    const pauseLabel = carousel.dataset.pauseLabel || autoplayButton?.getAttribute('aria-label') || '';
    const playLabel = carousel.dataset.playLabel || autoplayButton?.getAttribute('aria-label') || '';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (slides.length < 2) return;

    let currentIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let isPlaying = carousel.dataset.autoplay === 'true' && !reducedMotion && !Shopify.designMode;
    let timer;
    let preloadTimer;
    let touchStartX = 0;
    const speed = Number(carousel.dataset.autoplaySpeed) || 6000;

    const loadSlideImage = (slide) => {
      if (!slide || slide.dataset.imageReady === 'true') return;

      slide.querySelectorAll('[data-hero-srcset]').forEach((source) => {
        source.srcset = source.dataset.heroSrcset;
        source.removeAttribute('data-hero-srcset');
      });

      slide.querySelectorAll('[data-hero-src]').forEach((image) => {
        image.src = image.dataset.heroSrc;
        image.removeAttribute('data-hero-src');
      });

      slide.dataset.imageReady = 'true';
    };

    const queueNextImage = () => {
      const connection = navigator.connection;
      if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
      if (document.body.classList.contains('shouldi-entry-open')) {
        if (carousel.dataset.entryQueueBound !== 'true') {
          carousel.dataset.entryQueueBound = 'true';
          document.addEventListener(
            'shouldi:entry-closed',
            () => {
              delete carousel.dataset.entryQueueBound;
              queueNextImage();
            },
            { once: true }
          );
        }
        return;
      }
      window.clearTimeout(preloadTimer);
      preloadTimer = window.setTimeout(() => {
        loadSlideImage(slides[(currentIndex + 1) % slides.length]);
      }, 1200);
    };

    const setInteractiveState = (container, active) => {
      container.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
        if (active) {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', '-1');
        }
      });
    };

    const updateAutoplayButton = () => {
      if (!autoplayButton) return;
      autoplayButton.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
      autoplayButton.setAttribute('title', isPlaying ? pauseLabel : playLabel);
      if (pauseIcon) pauseIcon.hidden = !isPlaying;
      if (playIcon) playIcon.hidden = isPlaying;
    };

    const clearTimer = () => {
      window.clearInterval(timer);
      timer = undefined;
    };

    const startTimer = () => {
      clearTimer();
      if (document.body.classList.contains('shouldi-entry-open')) {
        if (carousel.dataset.entryAutoplayBound !== 'true') {
          carousel.dataset.entryAutoplayBound = 'true';
          document.addEventListener(
            'shouldi:entry-closed',
            () => {
              delete carousel.dataset.entryAutoplayBound;
              startTimer();
            },
            { once: true }
          );
        }
        return;
      }
      if (isPlaying && !document.hidden) {
        timer = window.setInterval(() => showSlide(currentIndex + 1), speed);
      }
    };

    const showSlide = (nextIndex, restart = false) => {
      currentIndex = (nextIndex + slides.length) % slides.length;
      loadSlideImage(slides[currentIndex]);

      slides.forEach((slide, index) => {
        const active = index === currentIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        setInteractiveState(slide, active);
      });

      captions.forEach((caption, index) => {
        const active = index === currentIndex;
        caption.classList.toggle('is-active', active);
        caption.setAttribute('aria-hidden', String(!active));
        setInteractiveState(caption, active);
      });

      dots.forEach((dot, index) => {
        const active = index === currentIndex;
        dot.classList.toggle('is-active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (count) {
        count.textContent = `${String(currentIndex + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
      }

      if (
        document.body.classList.contains('template-index') &&
        (!document.body.dataset.shouldiHomePanel || document.body.dataset.shouldiHomePanel === '1')
      ) {
        document.body.dataset.shouldiHomeHeaderTone = slides[currentIndex].dataset.headerTone || 'dark';
      }

      if (restart) startTimer();
      queueNextImage();
    };

    previousButton?.addEventListener('click', () => showSlide(currentIndex - 1, true));
    nextButton?.addEventListener('click', () => showSlide(currentIndex + 1, true));
    dots.forEach((dot, index) => dot.addEventListener('click', () => showSlide(index, true)));

    autoplayButton?.addEventListener('click', () => {
      isPlaying = !isPlaying;
      updateAutoplayButton();
      if (isPlaying) startTimer();
      else clearTimer();
    });

    carousel.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') showSlide(currentIndex - 1, true);
      if (event.key === 'ArrowRight') showSlide(currentIndex + 1, true);
    });

    carousel.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') touchStartX = event.clientX;
    });

    carousel.addEventListener('pointerup', (event) => {
      if (event.pointerType !== 'touch') return;
      const distance = event.clientX - touchStartX;
      if (Math.abs(distance) > 45) showSlide(currentIndex + (distance < 0 ? 1 : -1), true);
    });

    carousel.addEventListener('mouseenter', clearTimer);
    carousel.addEventListener('mouseleave', startTimer);
    carousel.addEventListener('focusin', clearTimer);
    carousel.addEventListener('focusout', (event) => {
      if (!carousel.contains(event.relatedTarget)) startTimer();
    });
    document.addEventListener('visibilitychange', () => (document.hidden ? clearTimer() : startTimer()));

    carousel.dataset.carouselReady = 'true';
    showSlide(currentIndex);
    updateAutoplayButton();
    startTimer();
  };

  const setupHeroCarousels = (scope = document) => {
    scope.querySelectorAll('shouldi-hero-carousel').forEach(setupHeroCarousel);
  };

  const setupHomeViewportHeight = () => {
    if (!document.body.classList.contains('template-index') || document.body.dataset.homeViewportReady === 'true') return;

    let resizeFrame;
    let settleTimer;
    let currentViewportHeight = 0;

    const queueViewportSettled = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('shouldi:home-viewport-settled', {
            detail: { height: currentViewportHeight },
          })
        );
      }, 180);
    };

    const syncViewportHeight = (force = false) => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        const viewportHeight = Math.round(window.visualViewport?.height || window.innerHeight);
        if (viewportHeight <= 0 || (!force && Math.abs(viewportHeight - currentViewportHeight) < 2)) return;

        currentViewportHeight = viewportHeight;
        document.documentElement.style.setProperty('--shouldi-home-viewport-height', `${viewportHeight}px`);
        document.dispatchEvent(
          new CustomEvent('shouldi:home-viewport-resized', {
            detail: { height: viewportHeight },
          })
        );
        queueViewportSettled();
      });
    };

    window.addEventListener('resize', () => syncViewportHeight(), { passive: true });
    window.addEventListener(
      'orientationchange',
      () => {
        currentViewportHeight = 0;
        window.setTimeout(() => syncViewportHeight(true), 120);
      },
      { passive: true }
    );
    window.addEventListener('pageshow', () => syncViewportHeight(), { passive: true });
    window.visualViewport?.addEventListener('resize', () => syncViewportHeight(), { passive: true });

    document.body.dataset.homeViewportReady = 'true';
    syncViewportHeight(true);
  };

  const setupHomePanelScroll = () => {
    if (!document.body.classList.contains('template-index') || document.body.dataset.homePanelScrollReady === 'true') return;

    const panels = [
      document.querySelector('.shouldi-home-hero-section'),
      document.querySelector('.shouldi-home-product-screen'),
      document.querySelector('.shouldi-home-story-screen'),
    ].filter(Boolean);
    if (panels.length !== 3) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let scrollLocked = false;
    let headerSyncFrame;
    let viewportAlignTimer;
    let activePanelIndex = 0;
    let touchActive = false;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartPanelIndex = 0;

    const syncHomeChatOffset = (storyActive) => {
      const chatSelectors = [
        '#ShopifyChat',
        'shopify-chat',
        '[data-shopify-chat]',
        'iframe[title*="chat" i]',
        'iframe[src*="inbox" i]',
      ];
      document.querySelectorAll(chatSelectors.join(',')).forEach((chat) => {
        if (storyActive && window.matchMedia('(max-width: 749px)').matches) {
          if (chat.dataset.shouldiHomeOriginalBottom === undefined) {
            chat.dataset.shouldiHomeOriginalBottom = chat.style.bottom || '';
          }
          chat.style.setProperty('bottom', 'calc(var(--shouldi-home-footer-height) + 8rem)', 'important');
        } else if (chat.dataset.shouldiHomeOriginalBottom !== undefined) {
          chat.style.bottom = chat.dataset.shouldiHomeOriginalBottom;
          delete chat.dataset.shouldiHomeOriginalBottom;
        }
      });
    };

    const getTop = (element) => element.getBoundingClientRect().top + window.scrollY;
    const getPanelIndex = () => {
      const currentTop = window.scrollY;
      return panels.reduce((closestIndex, panel, index) => {
        const currentDistance = Math.abs(getTop(panels[closestIndex]) - currentTop);
        const nextDistance = Math.abs(getTop(panel) - currentTop);
        return nextDistance < currentDistance ? index : closestIndex;
      }, 0);
    };
    const applyHeaderMode = (panelIndex) => {
      activePanelIndex = panelIndex;
      const heroActive = panelIndex === 0;
      const storyActive = panelIndex === panels.length - 1;
      document.body.dataset.shouldiHomeHeroActive = String(heroActive);
      document.body.dataset.shouldiHomeStoryActive = String(storyActive);
      document.body.dataset.shouldiHomePanel = String(panelIndex + 1);
      if (storyActive) {
        const storyPlayer = panels[2].querySelector('[data-story-player]');
        document.body.dataset.shouldiHomeHeaderTone = storyPlayer?.dataset.storyHeaderTone || 'light';
      }
      syncHomeChatOffset(storyActive);
    };
    const syncHeaderMode = () => applyHeaderMode(getPanelIndex());
    const queueHeaderSync = () => {
      window.cancelAnimationFrame(headerSyncFrame);
      headerSyncFrame = window.requestAnimationFrame(syncHeaderMode);
    };
    const alignPanelAfterViewportChange = () => {
      window.clearTimeout(viewportAlignTimer);
      viewportAlignTimer = window.setTimeout(() => {
        if (touchActive || scrollLocked) {
          alignPanelAfterViewportChange();
          return;
        }
        if (document.body.classList.contains('shouldi-entry-open')) return;

        window.scrollTo({ top: getTop(panels[activePanelIndex]), behavior: 'auto' });
        window.requestAnimationFrame(syncHeaderMode);
      }, 40);
    };
    const moveToIndex = (index) => {
      const nextIndex = Math.max(0, Math.min(index, panels.length - 1));
      applyHeaderMode(nextIndex);
      scrollLocked = true;
      window.scrollTo({ top: getTop(panels[nextIndex]), behavior: reducedMotion ? 'auto' : 'smooth' });
      window.setTimeout(() => {
        scrollLocked = false;
        syncHeaderMode();
      }, reducedMotion ? 100 : 900);
    };

    window.addEventListener(
      'wheel',
      (event) => {
        if (document.body.classList.contains('shouldi-entry-open') || event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
        event.preventDefault();
        if (scrollLocked || Math.abs(event.deltaY) < 6) return;
        moveToIndex(getPanelIndex() + Math.sign(event.deltaY));
      },
      { passive: false }
    );
    window.addEventListener('scroll', queueHeaderSync, { passive: true });
    window.addEventListener('resize', queueHeaderSync);
    window.addEventListener('pageshow', queueHeaderSync);
    document.addEventListener('shouldi:entry-closed', queueHeaderSync);
    document.addEventListener('shouldi:home-viewport-resized', queueHeaderSync);
    document.addEventListener('shouldi:home-viewport-settled', alignPanelAfterViewportChange);

    const chatObserver = new MutationObserver(queueHeaderSync);
    chatObserver.observe(document.body, { childList: true, subtree: true });

    window.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.touches[0];
        if (!touch) return;
        touchActive = true;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchStartPanelIndex = getPanelIndex();
      },
      { passive: true }
    );

    window.addEventListener(
      'touchend',
      (event) => {
        touchActive = false;
        if (scrollLocked || document.body.classList.contains('shouldi-entry-open')) return;
        const touch = event.changedTouches[0];
        if (!touch) return;
        const distanceX = touch.clientX - touchStartX;
        const distanceY = touch.clientY - touchStartY;
        if (Math.abs(distanceY) < 54 || Math.abs(distanceY) <= Math.abs(distanceX)) return;
        moveToIndex(touchStartPanelIndex + (distanceY < 0 ? 1 : -1));
      },
      { passive: true }
    );
    window.addEventListener('touchcancel', () => {
      touchActive = false;
    });

    window.addEventListener('keydown', (event) => {
      if (document.body.classList.contains('shouldi-entry-open') || ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
      if (event.key === 'PageDown' || event.key === 'ArrowDown' || (event.key === ' ' && !event.shiftKey)) {
        event.preventDefault();
        if (!scrollLocked) moveToIndex(getPanelIndex() + 1);
      }
      if (event.key === 'PageUp' || event.key === 'ArrowUp' || (event.key === ' ' && event.shiftKey)) {
        event.preventDefault();
        if (!scrollLocked) moveToIndex(getPanelIndex() - 1);
      }
    });

    document.body.dataset.homePanelScrollReady = 'true';
    syncHeaderMode();
  };

  const setupCompactFooters = (scope = document) => {
    scope.querySelectorAll('.shouldi-home-footer').forEach((footer) => {
      if (footer.dataset.compactFooterReady === 'true') return;

      footer.querySelectorAll('.shouldi-home-footer__group').forEach((group) => {
        group.addEventListener('toggle', () => {
          if (!group.open) return;
          footer.querySelectorAll('.shouldi-home-footer__group[open]').forEach((openGroup) => {
            if (openGroup !== group) openGroup.open = false;
          });
        });
      });

      footer.dataset.compactFooterReady = 'true';
    });
  };

  const setupStoryCarousel = (player) => {
    if (player.dataset.storyPlayerReady === 'true') return;

    const slides = [...player.querySelectorAll('[data-story-slide]')];
    if (slides.length === 0) return;

    const stage = player.querySelector('.shouldi-story-player__stage');
    const previousButton = player.querySelector('[data-story-previous]');
    const nextButton = player.querySelector('[data-story-next]');
    const toggleButton = player.querySelector('[data-story-toggle]');
    const pauseIcon = player.querySelector('[data-story-pause-icon]');
    const playIcon = player.querySelector('[data-story-play-icon]');
    const title = player.querySelector('.shouldi-story-player__copy [data-story-title]');
    const autoplay = player.dataset.autoplay === 'true' && slides.length > 1;
    const interval = Math.max(5000, Number(player.dataset.speed) || 7000);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let currentIndex = 0;
    let autoplayTimer;
    let userPaused = !autoplay || reduceMotion;
    let pointerId;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let pointerCurrentX = 0;
    let touchIdentifier;
    let dragAxis = '';
    let isDragging = false;
    let suppressClick = false;
    let isHovered = false;
    let hasFocus = false;
    let isVisible = false;

    const updateToggle = () => {
      if (!toggleButton) return;
      const label = userPaused ? toggleButton.dataset.playLabel : toggleButton.dataset.pauseLabel;
      toggleButton.setAttribute('aria-label', label);
      toggleButton.setAttribute('title', label);
      if (pauseIcon) pauseIcon.hidden = userPaused;
      if (playIcon) playIcon.hidden = !userPaused;
      toggleButton.classList.toggle('is-playing', !userPaused);
    };

    const clearAutoplay = () => {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = undefined;
    };

    const pauseAfterInteraction = () => {
      userPaused = true;
      clearAutoplay();
      updateToggle();
    };

    const canAutoplay = () =>
      autoplay && !reduceMotion && !userPaused && !isHovered && !hasFocus && !isDragging && isVisible && document.visibilityState === 'visible';

    const loadVisibleImages = () => {
      slides
        .filter((slide) =>
          ['is-active', 'is-previous', 'is-next'].some((className) => slide.classList.contains(className))
        )
        .forEach((slide) => {
          const image = slide.querySelector('img[loading="lazy"]');
          if (!image) return;
          image.loading = 'eager';
          image.fetchPriority = slide.classList.contains('is-active') ? 'high' : 'low';
        });
    };

    const scheduleAutoplay = () => {
      clearAutoplay();
      if (!canAutoplay()) return;
      autoplayTimer = window.setTimeout(() => showSlide(currentIndex + 1), interval);
    };

    const showSlide = (requestedIndex) => {
      currentIndex = (requestedIndex + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const forwardDistance = (slideIndex - currentIndex + slides.length) % slides.length;
        const backwardDistance = (currentIndex - slideIndex + slides.length) % slides.length;
        const link = slide.querySelector('a');

        slide.classList.remove('is-active', 'is-previous', 'is-next', 'is-far-previous', 'is-far-next', 'is-opposite');
        if (forwardDistance === 0) slide.classList.add('is-active');
        else if (forwardDistance === 1) slide.classList.add('is-next');
        else if (backwardDistance === 1) slide.classList.add('is-previous');
        else if (forwardDistance === 2) slide.classList.add('is-far-next');
        else if (backwardDistance === 2) slide.classList.add('is-far-previous');
        else slide.classList.add('is-opposite');

        const isActive = slideIndex === currentIndex;
        slide.setAttribute('aria-hidden', String(!isActive));
        if (link) link.tabIndex = isActive ? 0 : -1;
      });

      const activeSlide = slides[currentIndex];
      const url = activeSlide.dataset.storyUrl;
      const accentOne = activeSlide.dataset.storyAccentOne || '#aabec5';
      const accentTwo = activeSlide.dataset.storyAccentTwo || '#6f7f82';
      const headerTone = activeSlide.dataset.storyHeaderTone || 'light';
      if (title) {
        title.classList.remove('is-entering');
        title.textContent = activeSlide.dataset.storyTitle;
        title.href = url;
        if (!reduceMotion) {
          void title.offsetWidth;
          title.classList.add('is-entering');
        }
      }

      player.dataset.storyPlayerCurrent = String(currentIndex);
      player.dataset.storyHeaderTone = headerTone;
      player.style.setProperty('--story-accent-one', accentOne);
      player.style.setProperty('--story-accent-two', accentTwo);
      document.body.style.setProperty('--shouldi-story-footer-accent-one', accentOne);
      document.body.style.setProperty('--shouldi-story-footer-accent-two', accentTwo);
      player.style.setProperty('--story-drag-offset', '0px');
      if (document.body.dataset.shouldiHomeStoryActive === 'true') {
        document.body.dataset.shouldiHomeHeaderTone = headerTone;
      }
      if (isVisible) loadVisibleImages();
      scheduleAutoplay();
    };

    slides.forEach((slide, slideIndex) => {
      const link = slide.querySelector('a');
      link?.addEventListener('click', (event) => {
        if (suppressClick) {
          event.preventDefault();
          return;
        }
        if (slideIndex === currentIndex) return;
        event.preventDefault();
        event.stopPropagation();
        pauseAfterInteraction();
        showSlide(slideIndex);
      });
    });

    title?.addEventListener('animationend', () => title.classList.remove('is-entering'));

    previousButton?.addEventListener('click', () => {
      pauseAfterInteraction();
      showSlide(currentIndex - 1);
    });
    nextButton?.addEventListener('click', () => {
      pauseAfterInteraction();
      showSlide(currentIndex + 1);
    });
    toggleButton?.addEventListener('click', () => {
      userPaused = !userPaused;
      updateToggle();
      scheduleAutoplay();
    });

    stage?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        pauseAfterInteraction();
        showSlide(currentIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        pauseAfterInteraction();
        showSlide(currentIndex + 1);
      }
    });

    const finishDrag = (distanceX, allowSlideChange = true) => {
      suppressClick = dragAxis === 'x' && Math.abs(distanceX) > 8;
      isDragging = false;
      stage.classList.remove('is-dragging');
      player.style.setProperty('--story-drag-offset', '0px');
      if (allowSlideChange && dragAxis === 'x' && Math.abs(distanceX) > 44) {
        pauseAfterInteraction();
        showSlide(currentIndex + (distanceX < 0 ? 1 : -1));
      } else {
        scheduleAutoplay();
      }
      window.setTimeout(() => {
        suppressClick = false;
      }, 260);
    };

    const endDrag = (event) => {
      if (!stage || pointerId === undefined || (event.pointerId !== undefined && event.pointerId !== pointerId)) return;
      const distanceX = pointerCurrentX - pointerStartX;
      if (stage.hasPointerCapture?.(pointerId)) stage.releasePointerCapture(pointerId);
      pointerId = undefined;
      finishDrag(distanceX, event.type !== 'pointercancel');
    };

    stage?.addEventListener('dragstart', (event) => event.preventDefault());
    stage?.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0 || pointerId !== undefined) return;
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      pointerCurrentX = event.clientX;
      dragAxis = '';
      isDragging = true;
      stage.classList.add('is-dragging');
      stage.setPointerCapture?.(pointerId);
      clearAutoplay();
    });
    stage?.addEventListener(
      'pointermove',
      (event) => {
        if (!isDragging || event.pointerId !== pointerId) return;
        pointerCurrentX = event.clientX;
        const distanceX = pointerCurrentX - pointerStartX;
        const distanceY = event.clientY - pointerStartY;
        if (!dragAxis && Math.max(Math.abs(distanceX), Math.abs(distanceY)) > 7) {
          dragAxis = Math.abs(distanceX) > Math.abs(distanceY) ? 'x' : 'y';
        }
        if (dragAxis !== 'x') return;
        event.preventDefault();
        const dampedDistance = Math.max(-120, Math.min(120, distanceX * 0.72));
        player.style.setProperty('--story-drag-offset', `${dampedDistance}px`);
      },
      { passive: false }
    );
    stage?.addEventListener('pointerup', endDrag);
    stage?.addEventListener('pointercancel', endDrag);
    stage?.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1 || isDragging) return;
        const touch = event.touches[0];
        touchIdentifier = touch.identifier;
        pointerStartX = touch.clientX;
        pointerStartY = touch.clientY;
        pointerCurrentX = touch.clientX;
        dragAxis = '';
        isDragging = true;
        stage.classList.add('is-dragging');
        clearAutoplay();
      },
      { passive: true }
    );
    stage?.addEventListener(
      'touchmove',
      (event) => {
        if (!isDragging || touchIdentifier === undefined) return;
        const touch = Array.from(event.touches).find((candidate) => candidate.identifier === touchIdentifier);
        if (!touch) return;
        pointerCurrentX = touch.clientX;
        const distanceX = pointerCurrentX - pointerStartX;
        const distanceY = touch.clientY - pointerStartY;
        if (!dragAxis && Math.max(Math.abs(distanceX), Math.abs(distanceY)) > 7) {
          dragAxis = Math.abs(distanceX) > Math.abs(distanceY) ? 'x' : 'y';
        }
        if (dragAxis !== 'x') return;
        event.preventDefault();
        const dampedDistance = Math.max(-120, Math.min(120, distanceX * 0.72));
        player.style.setProperty('--story-drag-offset', `${dampedDistance}px`);
      },
      { passive: false }
    );

    const endTouchDrag = (event, allowSlideChange) => {
      if (!isDragging || touchIdentifier === undefined) return;
      const touch = Array.from(event.changedTouches).find((candidate) => candidate.identifier === touchIdentifier);
      if (!touch) return;
      pointerCurrentX = touch.clientX;
      touchIdentifier = undefined;
      finishDrag(pointerCurrentX - pointerStartX, allowSlideChange);
    };

    stage?.addEventListener('touchend', (event) => endTouchDrag(event, true), { passive: true });
    stage?.addEventListener('touchcancel', (event) => endTouchDrag(event, false), { passive: true });
    stage?.addEventListener('click', (event) => {
      if (suppressClick || event.defaultPrevented) return;

      const activeSlide = slides[currentIndex];
      const activeLink = activeSlide.querySelector('.shouldi-story-player__cover-link');
      if (activeLink?.contains(event.target)) return;

      const activeBounds = activeSlide.getBoundingClientRect();
      if (event.clientX < activeBounds.left) {
        pauseAfterInteraction();
        showSlide(currentIndex - 1);
      }
      if (event.clientX > activeBounds.right) {
        pauseAfterInteraction();
        showSlide(currentIndex + 1);
      }
    });

    player.addEventListener('mouseenter', () => {
      isHovered = true;
      clearAutoplay();
    });
    player.addEventListener('mouseleave', () => {
      isHovered = false;
      scheduleAutoplay();
    });
    player.addEventListener('focusin', () => {
      hasFocus = true;
      clearAutoplay();
    });
    player.addEventListener('focusout', (event) => {
      if (player.contains(event.relatedTarget)) return;
      hasFocus = false;
      scheduleAutoplay();
    });

    if ('IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          isVisible = entry.isIntersecting && entry.intersectionRatio > 0.45;
          player.classList.toggle('is-in-view', isVisible);
          if (isVisible) loadVisibleImages();
          scheduleAutoplay();
        },
        { threshold: [0, 0.45, 0.75] }
      );
      visibilityObserver.observe(player);
    } else {
      isVisible = true;
      player.classList.add('is-in-view');
    }

    document.addEventListener('visibilitychange', scheduleAutoplay);
    player.dataset.storyPlayerReady = 'true';
    updateToggle();
    showSlide(0);
  };

  const setupStoryCarousels = (scope = document) => {
    scope.querySelectorAll('[data-story-player]').forEach((player) => {
      try {
        setupStoryCarousel(player);
      } catch (error) {
        player.dataset.storyPlayerError = error instanceof Error ? error.message : 'Story player failed to initialize';
        console.error(error);
      }
    });
  };

  const setupProductGallery = (gallery) => {
    if (gallery.dataset.shouldiGalleryReady === 'true' || gallery.dataset.desktopLayout !== 'thumbnail_slider') return;

    const previousButton = gallery.querySelector('[data-product-media-previous]');
    const nextButton = gallery.querySelector('[data-product-media-next]');
    const count = gallery.querySelector('[data-product-media-count]');
    const mediaList = gallery.querySelector('.product__media-list');
    if (!previousButton || !nextButton || !count || !mediaList || typeof gallery.setActiveMedia !== 'function') return;

    const getMediaItems = () => [...mediaList.querySelectorAll(':scope > [data-media-id]')];
    const getActiveIndex = (items) => Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));

    const updateCount = () => {
      const items = getMediaItems();
      const hasMultipleMedia = items.length > 1;
      const controls = previousButton.closest('.shouldi-product-gallery__controls');
      if (controls) controls.hidden = !hasMultipleMedia;
      previousButton.disabled = !hasMultipleMedia;
      nextButton.disabled = !hasMultipleMedia;
      previousButton.setAttribute('aria-disabled', String(!hasMultipleMedia));
      nextButton.setAttribute('aria-disabled', String(!hasMultipleMedia));
      count.textContent = `${getActiveIndex(items) + 1} / ${items.length}`;
    };

    const showMedia = (offset) => {
      const items = getMediaItems();
      if (items.length < 2) return;

      const activeIndex = getActiveIndex(items);
      const nextIndex = (activeIndex + offset + items.length) % items.length;
      gallery.setActiveMedia(items[nextIndex].dataset.mediaId, false);
      updateCount();
    };

    previousButton.addEventListener('click', () => showMedia(-1));
    nextButton.addEventListener('click', () => showMedia(1));

    const activeMediaObserver = new MutationObserver(updateCount);
    getMediaItems().forEach((item) => activeMediaObserver.observe(item, { attributes: true, attributeFilter: ['class'] }));

    gallery.dataset.shouldiGalleryReady = 'true';
    updateCount();
  };

  const setupProductGalleries = (scope = document) => {
    scope.querySelectorAll('media-gallery[data-desktop-layout="thumbnail_slider"]').forEach(setupProductGallery);
  };

  const normalizeSize = (value) => {
    const text = value.toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();
    const size = text.match(/\b(XXS|XXL|XS|XL|S|M|L)\b/)?.[1];
    if (!size) return text;
    return `${size}${text.includes('PETITE') ? ' PETITE' : ''}`;
  };

  const setupSizeGuides = (scope = document) => {
    scope.querySelectorAll('.shouldi-size-guide[data-available-sizes]').forEach((guide) => {
      if (guide.dataset.sizeGuideReady === 'true') return;

      const availableSizes = new Set(
        guide.dataset.availableSizes
          .split('|')
          .map(normalizeSize)
          .filter(Boolean)
      );

      if (availableSizes.size > 0) {
        const rows = [...guide.querySelectorAll('tbody tr')];
        const rowSizes = rows.map((row) => normalizeSize(row.querySelector('th, td')?.textContent || ''));
        const simpleSizePattern = /^(XXS|XXL|XS|XL|S|M|L)( PETITE)?$/;

        if (rows.length > 0 && rowSizes.every((size) => simpleSizePattern.test(size))) {
          rows.forEach((row, index) => {
            if (!availableSizes.has(rowSizes[index])) row.remove();
          });
        }
      }

      guide.dataset.sizeGuideReady = 'true';
    });
  };

  const setupProductTabs = (scope = document) => {
    scope.querySelectorAll('[data-product-tabs]').forEach((tabs) => {
      if (tabs.dataset.productTabsReady === 'true') return;

      const tabList = tabs.querySelector('[role="tablist"]');
      const tabButtons = [...tabs.querySelectorAll('[role="tab"]')];
      const tabPanels = [...tabs.querySelectorAll('[role="tabpanel"]')];
      if (!tabList || tabButtons.length === 0 || tabPanels.length === 0) return;

      const activateTab = (nextIndex, moveFocus = false, scrollTab = false) => {
        const normalizedIndex = (nextIndex + tabButtons.length) % tabButtons.length;

        tabButtons.forEach((button, index) => {
          const isActive = index === normalizedIndex;
          button.setAttribute('aria-selected', String(isActive));
          button.tabIndex = isActive ? 0 : -1;
          if (isActive && scrollTab) {
            button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        });

        tabPanels.forEach((panel, index) => {
          panel.hidden = index !== normalizedIndex;
        });

        if (moveFocus) tabButtons[normalizedIndex].focus();
      };

      tabButtons.forEach((button, index) => {
        button.addEventListener('click', () => activateTab(index, false, true));
      });

      tabList.addEventListener('keydown', (event) => {
        const currentIndex = tabButtons.indexOf(document.activeElement);
        if (currentIndex < 0) return;

        let nextIndex;
        if (event.key === 'ArrowRight') nextIndex = currentIndex + 1;
        if (event.key === 'ArrowLeft') nextIndex = currentIndex - 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabButtons.length - 1;
        if (nextIndex === undefined) return;

        event.preventDefault();
        activateTab(nextIndex, true, true);
      });

      let touchStartX = 0;
      let touchStartY = 0;
      tabs.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.touches[0];
          if (!touch) return;
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
        },
        { passive: true }
      );
      tabs.addEventListener(
        'touchend',
        (event) => {
          const touch = event.changedTouches[0];
          if (!touch) return;
          const distanceX = touch.clientX - touchStartX;
          const distanceY = touch.clientY - touchStartY;
          if (Math.abs(distanceX) < 56 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.2) return;

          const currentIndex = Math.max(
            0,
            tabButtons.findIndex((button) => button.getAttribute('aria-selected') === 'true')
          );
          activateTab(currentIndex + (distanceX < 0 ? 1 : -1), false, true);
        },
        { passive: true }
      );

      tabs.dataset.productTabsReady = 'true';
      activateTab(Math.max(0, tabButtons.findIndex((button) => button.getAttribute('aria-selected') === 'true')));
    });
  };

  const setupProductTitleFit = (scope = document) => {
    scope.querySelectorAll('.product__info-container .product__title h1').forEach((title) => {
      if (title.dataset.titleFitReady === 'true') return;

      let resizeFrame;
      let lastWidth = 0;

      const fitTitle = () => {
        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(() => {
          const availableWidth = title.clientWidth;
          if (Math.abs(availableWidth - lastWidth) < 1 && title.dataset.titleFitComplete === 'true') return;
          lastWidth = availableWidth;

          title.style.removeProperty('font-size');
          title.dataset.titleFitted = 'false';
          title.dataset.titleWrap = 'false';

          if (window.matchMedia('(max-width: 749px)').matches || availableWidth <= 0) {
            title.dataset.titleFitComplete = 'true';
            return;
          }

          title.dataset.titleFitted = 'true';
          const naturalFontSize = Number.parseFloat(window.getComputedStyle(title).fontSize);
          const requiredWidth = title.scrollWidth;

          if (requiredWidth > availableWidth + 1) {
            const minimumFontSize = 24;
            const fittedFontSize = Math.max(
              minimumFontSize,
              Math.floor(((naturalFontSize * availableWidth) / requiredWidth) * 9.6) / 10
            );
            title.style.fontSize = `${fittedFontSize}px`;

            if (title.scrollWidth > availableWidth + 1) {
              title.dataset.titleFitted = 'false';
              title.dataset.titleWrap = 'true';
              title.style.removeProperty('font-size');
            }
          }

          title.dataset.titleFitComplete = 'true';
        });
      };

      const resizeObserver = new ResizeObserver(fitTitle);
      resizeObserver.observe(title.parentElement || title);
      window.addEventListener('resize', fitTitle);
      title.dataset.titleFitReady = 'true';
      fitTitle();
    });
  };


  // Newsletter subscription via Klaviyo API + toast notification
  const setupNewsletterForm = (scope = document) => {
    const forms = scope.querySelectorAll('form.shouldi-home-footer__newsletter');
    if (!forms.length) return;

    const KLAVIYO_COMPANY_ID = 'XyuMBv';

    let toastContainer = document.getElementById('shouldi-toast-container');
    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.id = 'shouldi-toast-container';
      toastContainer.style.cssText = 'position: fixed; top: 24px; right: 24px; z-index: 9999; display: flex; flex-direction: column; gap: 12px; pointer-events: none;';
      document.body.appendChild(toastContainer);
    }

    const showToast = (message, type = 'success') => {
      const toast = document.createElement('div');
      const bgColor = type === 'success' ? '#1a1a1a' : '#c53030';
      toast.style.cssText = 'background:' + bgColor + ';color:#fff;padding:14px 20px;border-radius:4px;font-size:13px;font-family:Inter,-apple-system,sans-serif;letter-spacing:.3px;box-shadow:0 4px 20px rgba(0,0,0,.15);pointer-events:auto;opacity:0;transform:translateX(100%);transition:opacity .3s ease,transform .3s ease;max-width:320px;line-height:1.5;';
      toast.textContent = message;
      toastContainer.appendChild(toast);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          toast.style.opacity = '1';
          toast.style.transform = 'translateX(0)';
        });
      });

      setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(100%)';
        setTimeout(() => toast.remove(), 300);
      }, 4000);
    };

    const validateEmail = (email) => {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const subscribeWithKlaviyo = async (email) => {
      // Try using _learnq (Klaviyo.js) if available
      if (window._learnq && typeof window._learnq.push === 'function') {
        try {
          window._learnq.push(['identify', { $email: email, $source: 'Footer Newsletter' }]);
          window._learnq.push(['track', 'Subscribed to Newsletter', {
            Source: 'Footer',
            List: 'Newsletter'
          }]);
          return true;
        } catch (e) { /* fall through to API */ }
      }

      // Fallback: Create profile via Client API and track event
      try {
        // Create/update profile
        const profileResp = await fetch('https://a.klaviyo.com/client/profiles/?company_id=' + KLAVIYO_COMPANY_ID, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'revision': '2023-01-01' },
          body: JSON.stringify({
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: { $source: 'Footer Newsletter' }
              }
            }
          }),
        });

        if (profileResp.ok) {
          // Track subscription event
          try {
            await fetch('https://a.klaviyo.com/client/events/?company_id=' + KLAVIYO_COMPANY_ID, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'revision': '2023-01-01' },
              body: JSON.stringify({
                data: [{
                  type: 'event',
                  attributes: {
                    profile: { data: { type: 'profile', attributes: { email } } },
                    metric: { data: { type: 'metric', attributes: { name: 'Subscribed to Newsletter' } } },
                    properties: { Source: 'Footer', List: 'Newsletter' }
                  }
                }]
              }),
            });
          } catch (e) { /* best effort */ }
          return true;
        }
        return false;
      } catch (e) {
        return false;
      }
    };

    forms.forEach((form) => {
      if (form.dataset.klaviyoReady === 'true') return;
      form.dataset.klaviyoReady = 'true';

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const emailInput = form.querySelector('input[type="email"]');
        const email = emailInput.value.trim();

        if (!email || !validateEmail(email)) {
          showToast('Please enter a valid email address.', 'error');
          return;
        }

        const submitBtn = form.querySelector('button[type="submit"]');
        const originalHTML = submitBtn.innerHTML;
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span aria-hidden="true">&hellip;</span>';

        try {
          const success = await subscribeWithKlaviyo(email);
          if (success) {
            showToast('Thanks for subscribing! Keep an eye on your inbox for the next drop.');
            emailInput.value = '';
          } else {
            showToast('Something went wrong. Please try again.', 'error');
          }
        } catch (err) {
          showToast('Something went wrong. Please try again.', 'error');
        } finally {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalHTML;
        }
      });
    });
  };

  setupNewsletterForm();

  ready(() => {
    setupEntryScreens();
    setupMobileAddToCart();
    setupHeroCarousels();
    setupHomeViewportHeight();
    setupHomePanelScroll();
    setupCompactFooters();
    setupStoryCarousels();
    setupProductGalleries();
    setupSizeGuides();
    setupProductTabs();
    setupProductTitleFit();
  });

  document.addEventListener('shopify:section:load', (event) => {
    setupEntryScreens(event.target);
    setupHeroCarousels(event.target);
    setupCompactFooters(event.target);
    setupStoryCarousels(event.target);
    setupProductGalleries(event.target);
    setupSizeGuides(event.target);
    setupProductTabs(event.target);
    setupProductTitleFit(event.target);
    setupNewsletterForm(event.target);
  });
})();
